import React, { useState } from 'react';
import camelcase from 'camelcase-es5';

import { Link } from '../Link';

import InputLabel from '../InputLabel';

import TextFieldFormControl from './TextFieldFormControl';
import TextFieldInput from './TextFieldInput';
import TextFieldFooter from './TextFieldFooter';
import TextFieldBase from './TextFieldBase';
import useFormControlReducer from '../../hooks/useFormControlReducer';
import TextFieldSuffix from './TextFieldSuffix';
import TPasswordTextField from './@types/PasswordTextField';
import generateTextFieldIds, { isCharsExceeded } from './generateTextFieldIds';

const PasswordTextField = ({
  errorText,
  helperText,
  optionalText,
  inputRef,
  isRequired,
  labelText = '',
  showPasswordText,
  hidePasswordText,
  charsRemainingText,
  charsExceededText,
  onChange,
  onFocus,
  onBlur,
  onMouseOut,
  onMouseOver,
  onKeyPress,
  value,
  id = `PasswordTextField-${camelcase(labelText)}`,
  name,
  dataTestId,
  ariaLabel,
  ariaLabelledBy,
  ariaLive,
  maxLength,
}: TPasswordTextField) => {
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const togglePasswordVisibility = () => setPasswordVisibility(!isPasswordVisible);
  const { state, handlers } = useFormControlReducer({
    onChange,
    onFocus,
    onBlur,
    onMouseOut,
    onMouseOver,
    errorText,
    value,
  });

  const { isEmpty, isErrored, isFocused, characterCount } = state;
  const { handleChange, handleFocus, handleBlur, handleMouseOver, handleMouseOut } = handlers;

  const charsExceeded = isCharsExceeded(characterCount, maxLength);
  const { helperTextId, errorTextId, charsExceededId } = generateTextFieldIds(id, Boolean(errorText), charsExceeded);

  return (
    <TextFieldBase kind="password" ariaLive={ariaLive}>
      {labelText && (
        <InputLabel htmlFor={id} isInputRequired={isRequired} labelText={labelText} optionalText={optionalText} />
      )}

      <TextFieldFormControl isFocused={isFocused} isErrored={isErrored} isEmpty={isEmpty}>
        <TextFieldInput
          type={isPasswordVisible ? 'text' : 'password'}
          id={id}
          name={name}
          ref={inputRef}
          isRequired={isRequired}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          onKeyPress={onKeyPress}
          data-testid={dataTestId}
          ariaLabel={ariaLabel}
          ariaLabelledBy={ariaLabelledBy}
          helperTextId={helperTextId}
          errorTextId={errorTextId}
          charsExceededId={charsExceededId}
        />
        <TextFieldSuffix>
          <Link onClick={togglePasswordVisibility}>{isPasswordVisible ? hidePasswordText : showPasswordText}</Link>
        </TextFieldSuffix>
      </TextFieldFormControl>
      <TextFieldFooter
        helperText={helperText}
        errorText={errorText}
        charsRemainingText={charsRemainingText}
        charsExceededText={charsExceededText}
        characterCount={characterCount}
        id={helperTextId}
        errorTextId={errorTextId}
        charsExceededId={charsExceededId}
        charsExceeded={charsExceeded}
      />
    </TextFieldBase>
  );
};

export default PasswordTextField;
